import { useDebounceFn } from '@vueuse/core';
import type Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import type Style from 'ol/style/Style';

import type { DrawLayerResponseDTO, GeoJsonFeatureDTO } from '@nora-monorepo/nora-api-client';

import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';
import { BBOX_RELOAD_DEBOUNCE_MS, BBOX_RELOAD_MAX_WAIT_MS } from '#nora-ui/map/map/constants';
import { NoraLayerManager } from '#nora-ui/map/map/layer-managers/NoraLayerManager';
import { MapSetup } from '#nora-ui/map/map/MapSetup';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';

/**
 * What a draw feature sync does to the source, collected before touching it.
 */
interface DrawFeatureSync {
  /**
   * Features on the source after the sync, by feature id.
   */
  rendered: Map<string, Feature>;
  /**
   * Feature ids taken by an earlier draw layer; a feature is rendered once.
   */
  claimedFeatureIds: Set<string>;
  /**
   * GeoJSON features to read into OpenLayers, with their draw layer.
   */
  pending: { geoJson: GeoJsonFeatureDTO; drawLayer: DrawLayerResponseDTO }[];
  /**
   * Previously read features that are not on the source and are added back.
   */
  reused: Feature[];
}

export class DrawLayerManager extends NoraLayerManager {
  readonly LAYER_ID = 'drawLayer';
  readonly LAYER_NAME = 'NORA Drawings';
  override readonly isClickable = true;

  private BASE_Z_INDEX = 1000;

  /**
   * Spacing between the style-zIndex buckets of two adjacent draw layers. Must exceed the
   * highest zIndex any individual style function uses (labels use 1000), so a feature's
   * internal style stacking stays intact within its layer's bucket.
   */
  private static readonly LAYER_ORDER_Z_SCALE = 100_000;

  /**
   * Original zIndex of each style before applyLayerOrderZIndex rescaled it. Some style
   * functions (e.g. the ruler) return shared module-level Style instances, so rescaling
   * from the *current* zIndex would compound across features.
   */
  private originalStyleZIndexes = new WeakMap<Style, number>();

  protected source: VectorSource;
  protected layer: VectorLayer<VectorSource>;

  /**
   * The OpenLayers feature read from each GeoJSON feature object, kept after it leaves the
   * source so it can be re-added without reading it again when its tile comes back into view.
   * Entries go away with the tile cache's objects.
   */
  private featuresByGeoJson = new WeakMap<GeoJsonFeatureDTO, Feature>();

  private debouncedLoadCurrentBbox = useDebounceFn(async (): Promise<void> => {
    await this.map.state.draw.loadCurrentBbox();
  }, BBOX_RELOAD_DEBOUNCE_MS, {
    maxWait: BBOX_RELOAD_MAX_WAIT_MS,
  });

  constructor(private readonly map: NoraMap) {
    super();

    this.source = new VectorSource();
    this.layer = new VectorLayer({
      source: this.source,
      style: feature => this.applyLayerOrderZIndex(feature, getFeatureStyle(feature)),
    });
  }

  /**
   * Lifts every style of a feature into a style-zIndex bucket derived from the per-feature
   * `zIndex` property that recalculateOperationDrawLayerZIndex assigns from the draw layer
   * order. Style zIndex is the only ordering OpenLayers applies across builder types —
   * within one zIndex it always paints fills before icons before text, regardless of
   * feature order — so bucketing per draw layer is what makes "layer A above layer B"
   * hold for mixed feature types.
   */
  private applyLayerOrderZIndex(feature: FeatureLike, styles: Style | Style[]): Style | Style[] {
    const layerOrderZIndex = (feature.get('zIndex') as number | undefined) ?? 0;
    const styleArray = Array.isArray(styles) ? styles : [styles];

    for (const style of styleArray) {
      let originalZIndex = this.originalStyleZIndexes.get(style);

      if (originalZIndex === undefined) {
        originalZIndex = style.getZIndex() ?? 0;
        this.originalStyleZIndexes.set(style, originalZIndex);
      }

      style.setZIndex(layerOrderZIndex * DrawLayerManager.LAYER_ORDER_Z_SCALE + originalZIndex);
    }

    return styles;
  }

  override getLayer(): VectorLayer<VectorSource> {
    return this.layer;
  }

  override async setup(): Promise<void> {
    await this.setupPanHandler();
  }

  /**
   * Load/reload draw layer features from state, syncing the source incrementally.
   */
  override async load(): Promise<void> {
    this.syncDrawFeatures();
  }

  /**
   * Unload/clear all draw layer features.
   */
  override unload(): void {
    this.source.clear();
  }

  setDrawLayerVisibility(drawLayerId: string, isVisible: boolean): void {
    if (isVisible) {
      this.showDrawLayer(drawLayerId);
    }
    else {
      this.hideDrawLayer(drawLayerId);
    }
  }

  showDrawLayer(drawLayerId: string): void {
    this.source.forEachFeature((feature) => {
      if (feature.get('drawLayerId') === drawLayerId) {
        feature.unset('hidden');
      }
    });
  }

  hideDrawLayer(drawLayerId: string): void {
    this.source.forEachFeature((feature) => {
      if (feature.get('drawLayerId') === drawLayerId) {
        feature.set('hidden', true);
      }
    });
  }

  recalculateOperationDrawLayerZIndex(): void {
    const drawLayers = this.map.state.draw.drawLayers;

    // Index the target zIndex per draw layer once, then walk the source a single time
    const zIndexByDrawLayerId = new Map<string, number>();

    drawLayers.forEach((drawLayer, index) => {
      zIndexByDrawLayerId.set(drawLayer.id, this.BASE_Z_INDEX - index);
    });

    this.source.forEachFeature((feature) => {
      const drawLayerId = feature.get('drawLayerId') as string | undefined;

      if (drawLayerId === undefined) {
        return;
      }

      const zIndex = zIndexByDrawLayerId.get(drawLayerId);

      if (zIndex !== undefined) {
        feature.set('zIndex', zIndex);
      }
    });
  }

  private async setupPanHandler(): Promise<void> {
    this.map.getOlMapOrThrow().on('moveend', async () => {
      await this.debouncedLoadCurrentBbox();
    });
  }

  /**
   * Brings the source in line with the draw layers' features without clearing it. A feature
   * is kept when it was read from the very same GeoJSON object: the tile cache hands out the
   * same objects for unchanged tiles, while refetched tiles and local patches produce new
   * ones. Features whose GeoJSON object was read before are kept or re-added as-is; only new
   * or changed features are read into OpenLayers, and only stale ones removed.
   */
  private syncDrawFeatures(): void {
    const sync = this.collectDrawFeatureSync();
    const added = this.readPendingFeatures(sync);

    this.replaceStaleFeatures(sync.rendered, [...sync.reused, ...added]);
    this.recalculateOperationDrawLayerZIndex();
  }

  /**
   * Decides per feature of every draw layer whether it is kept, re-added or read anew,
   * without touching the source yet.
   */
  private collectDrawFeatureSync(): DrawFeatureSync {
    const editingFeatureId = this.map.drawToolUI.getEditingFeatureId();
    const sync: DrawFeatureSync = { rendered: new Map(), claimedFeatureIds: new Set(), pending: [], reused: [] };

    for (const drawLayer of this.map.state.draw.drawLayers) {
      this.collectDrawLayerFeatureSync(sync, drawLayer, editingFeatureId);
    }

    return sync;
  }

  /**
   * Adds a draw layer's features to the sync, skipping the feature being edited and features
   * already claimed by an earlier layer.
   */
  private collectDrawLayerFeatureSync(sync: DrawFeatureSync, drawLayer: DrawLayerResponseDTO, editingFeatureId: string | undefined): void {
    const hidden = !this.map.state.draw.isDrawLayerVisible(drawLayer.id);

    for (const geoJson of drawLayer.geoJson?.features ?? []) {
      if (geoJson.id === editingFeatureId || sync.claimedFeatureIds.has(geoJson.id)) {
        continue;
      }

      sync.claimedFeatureIds.add(geoJson.id);
      this.collectFeatureSync(sync, geoJson, drawLayer, hidden);
    }
  }

  /**
   * Reuses the feature previously read from this GeoJSON object for this draw layer, updating
   * its visibility; otherwise marks the GeoJSON to be read.
   */
  private collectFeatureSync(sync: DrawFeatureSync, geoJson: GeoJsonFeatureDTO, drawLayer: DrawLayerResponseDTO, hidden: boolean): void {
    const previouslyRead = this.featuresByGeoJson.get(geoJson);

    if (previouslyRead?.get('drawLayerId') !== drawLayer.id) {
      sync.pending.push({ geoJson, drawLayer });

      return;
    }

    if (Boolean(previouslyRead.get('hidden')) !== hidden) {
      previouslyRead.set('hidden', hidden);
    }

    // Checked on the source itself: other code removes features from it too, e.g. editing
    // a feature removes it and cancelling the edit restores a clone in its place.
    if (!this.source.hasFeature(previouslyRead)) {
      sync.reused.push(previouslyRead);
    }

    sync.rendered.set(geoJson.id, previouslyRead);
  }

  /**
   * Reads the pending GeoJSON features into OpenLayers in one go, registering them as
   * rendered and as read from their GeoJSON object.
   */
  private readPendingFeatures(sync: DrawFeatureSync): Feature[] {
    const added = new GeoJSON().readFeatures({
      type: 'FeatureCollection',
      features: sync.pending.map(({ geoJson, drawLayer }) => this.transformFeatureForDrawLayer(drawLayer, geoJson)),
    }, {
      dataProjection: MapSetup.WGS84,
      featureProjection: MapSetup.WEB_MERCATOR,
    });

    sync.pending.forEach(({ geoJson }, index) => {
      const feature = added[index];

      sync.rendered.set(geoJson.id, feature);
      this.featuresByGeoJson.set(geoJson, feature);
    });

    return added;
  }

  /**
   * Removes the source's features that are no longer rendered, then adds the given ones.
   */
  private replaceStaleFeatures(rendered: Map<string, Feature>, featuresToAdd: Feature[]): void {
    const keptFeatures = new Set(rendered.values());
    const staleFeatures = this.source.getFeatures().filter(feature => !keptFeatures.has(feature));

    if (staleFeatures.length > 0) {
      this.source.removeFeatures(staleFeatures);
    }

    if (featuresToAdd.length > 0) {
      this.source.addFeatures(featuresToAdd);
    }
  }

  private transformFeatureForDrawLayer(drawLayer: DrawLayerResponseDTO, feature: GeoJsonFeatureDTO) {
    const { id, ...featureData } = feature;

    const isDrawLayerVisible = this.map.state.draw.isDrawLayerVisible(drawLayer.id);

    return {
      ...featureData,
      id,
      properties: {
        ...feature.properties,
        id,
        drawLayerId: drawLayer.id,
        hidden: !isDrawLayerVisible,
      },
    };
  }
}
